import { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import {
  DialogTrigger,
  ActionButton,
  Avatar,
  Popover,
  Divider,
  Menu,
  MenuSection,
  MenuItem,
  SubmenuTrigger,
  Text,
} from "@react-spectrum/s2";

import { useStore } from "zustand";

import { useIMS } from "@app/hooks/useIMS";
import { useIsAdmin, useIsDevMode, useUserRoles } from "@app/hooks/useRoles";

import { store } from "../store";

import { style } from "@react-spectrum/s2/style" with { type: "macro" };

const COLOR_SCHEME_LABELS = {
  system: "System",
  light: "Light",
  dark: "Dark",
};

const ADMIN_MENU_KEYS = {
  disableRoles: "disable-roles",
  devMode: "dev-mode",
  demoMode: "demo-mode",
};

const AvatarWithFallback = ({ profile, size, ...props }) => {
  const [currentSrc, setCurrentSrc] = useState(
    `https://ab.adobe-identity.com/api/avatars/100/${encodeURIComponent(profile?.id)}`,
  );

  const ref = useRef(null);

  useEffect(() => {
    if (ref.current) {
      const domNode = ref.current?.UNSAFE_getDOMNode();

      if (domNode) {
        domNode.querySelector("img").addEventListener(
          "error",
          () => {
            setCurrentSrc(
              `https://s7d2.scene7.com/is/image/IMGDIR/${profile?.email?.replace("@adobe.com", "")}?$120x$`,
            );
          },
          { once: true },
        );
      }
    }
  }, [profile?.email]);

  return <Avatar ref={ref} src={currentSrc} size={size} {...props} />;
};

AvatarWithFallback.propTypes = {
  profile: PropTypes.shape({
    id: PropTypes.string,
    email: PropTypes.string,
  }),
  size: PropTypes.number,
};

export const UserMenu = () => {
  const profile = useStore(store, (state) => state?.ims?.profile);

  const colorSchemePreference = useStore(
    store,
    (state) => state.colorSchemePreference,
  );
  const setColorSchemePreference = useStore(
    store,
    (state) => state.setColorSchemePreference,
  );

  // Real (non-overridden) roles keep the Admin submenu visible so admins can
  // switch roles back on.
  const isAdmin = useIsAdmin({ ignoreOverride: true });
  const assignedRoles = useUserRoles({ ignoreOverride: true });
  const rolesDisabled = useStore(store, (state) => state.rolesDisabled);
  const setRolesDisabled = useStore(store, (state) => state.setRolesDisabled);
  const setDevModeOverride = useStore(
    store,
    (state) => state.setDevModeOverride,
  );
  const isDevMode = useIsDevMode();
  const demoMode = useStore(store, (state) => state.demoMode);
  const setDemoMode = useStore(store, (state) => state.setDemoMode);

  const adminSelectedKeys = [
    ...(rolesDisabled ? [ADMIN_MENU_KEYS.disableRoles] : []),
    ...(isDevMode ? [ADMIN_MENU_KEYS.devMode] : []),
    ...(demoMode ? [ADMIN_MENU_KEYS.demoMode] : []),
  ];

  const handleAdminSelectionChange = (keys) => {
    const nextRolesDisabled = keys.has(ADMIN_MENU_KEYS.disableRoles);
    const nextDevMode = keys.has(ADMIN_MENU_KEYS.devMode);
    const nextDemoMode = keys.has(ADMIN_MENU_KEYS.demoMode);
    if (nextDemoMode !== demoMode) {
      setDemoMode(nextDemoMode);
    }
    if (nextRolesDisabled !== rolesDisabled) {
      setRolesDisabled(nextRolesDisabled);
    }
    if (nextDevMode !== isDevMode) {
      setDevModeOverride(nextDevMode);
    }
  };

  const { signOut } = useIMS();

  if (!profile?.email) {
    return null;
  }
  return (
    <DialogTrigger>
      <ActionButton isQuiet size="L" aria-label="Account">
        <AvatarWithFallback profile={profile} size={28} />
      </ActionButton>
      <Popover hideArrow placement="bottom end">
        <div
          className={style({
            paddingTop: 4,
            display: "flex",
            flexDirection: "column",
            gap: 12,
          })}
        >
          <div
            className={style({
              display: "flex",
              gap: 12,
              alignItems: "center",
              marginX: 12,
            })}
          >
            <AvatarWithFallback profile={profile} size={56} />
            <div>
              <div
                className={style({
                  font: "title",
                })}
              >
                {profile?.fullName ?? ""}
              </div>
              <div
                className={style({
                  font: "ui",
                })}
              >
                {profile?.email ?? ""}
              </div>
            </div>
          </div>
          <Divider
            styles={style({
              marginX: 12,
            })}
          />
          <Menu aria-label="Settings">
            <SubmenuTrigger>
              <MenuItem>
                <Text slot="label">Theme</Text>
                <Text slot="value">
                  {COLOR_SCHEME_LABELS[colorSchemePreference] ??
                    colorSchemePreference}
                </Text>
              </MenuItem>
              <Menu
                selectionMode="single"
                selectedKeys={[colorSchemePreference]}
                onSelectionChange={(keys) => {
                  const key = [...keys][0];
                  if (key) {
                    setColorSchemePreference(key);
                  }
                }}
              >
                {Object.entries(COLOR_SCHEME_LABELS).map(([id, label]) => (
                  <MenuItem key={id} id={id}>
                    {label}
                  </MenuItem>
                ))}
              </Menu>
            </SubmenuTrigger>

            {isAdmin && (
              <SubmenuTrigger>
                <MenuItem>
                  <Text slot="label">Admin</Text>
                </MenuItem>
                <Menu
                  aria-label="Admin"
                  selectionMode="multiple"
                  selectedKeys={adminSelectedKeys}
                  onSelectionChange={handleAdminSelectionChange}
                >
                  <MenuItem
                    id={ADMIN_MENU_KEYS.disableRoles}
                    textValue="Disable roles"
                  >
                    <Text slot="label">Disable roles</Text>
                    <Text slot="description">
                      {assignedRoles.join(", ") || "No roles"}
                    </Text>
                  </MenuItem>
                  <MenuItem id={ADMIN_MENU_KEYS.demoMode} textValue="Demo mode">
                    <Text slot="label">Demo mode</Text>
                    <Text slot="description">
                      Replay completed projects without changing them
                    </Text>
                  </MenuItem>
                  <MenuItem id={ADMIN_MENU_KEYS.devMode} textValue="Dev mode">
                    <Text slot="label">Dev mode</Text>
                  </MenuItem>
                </Menu>
              </SubmenuTrigger>
            )}

            <MenuItem
              onClick={() => {
                signOut();
              }}
            >
              Sign out
            </MenuItem>
          </Menu>
        </div>
      </Popover>
    </DialogTrigger>
  );
};
